import { useState } from 'react';
import { canCreateChildOrg } from '../../utils/rbac';
import type { Organization } from '../../types/organization';
import styles from './AdminPage.module.css';

type OrgLevel = 'federal' | 'canton' | 'commune';

interface OrgFormData {
  name: string;
  code: string;
  level: OrgLevel;
  parent_id: string | null;
}

interface Props {
  /** Pass an existing org to enter edit mode; omit for create mode. */
  organization?: Organization;
  /** Flat list of all orgs (for parent selection). */
  organizations: Organization[];
  /** Admin's org level (null = federal / no org). */
  adminOrgLevel: string | null;
  onSubmit: (data: OrgFormData) => Promise<void>;
  onCancel: () => void;
}

export function OrgForm({ organization, organizations, adminOrgLevel, onSubmit, onCancel }: Props) {
  const isEdit = !!organization;

  const [formData, setFormData] = useState<OrgFormData>({
    name: organization?.name ?? '',
    code: organization?.code ?? '',
    level: (organization?.level as OrgLevel) ?? 'federal',
    parent_id: organization?.parent_id ?? null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const availableLevels = (['federal', 'canton', 'commune'] as const).filter((level) =>
    canCreateChildOrg(adminOrgLevel, level),
  );

  const possibleParents = organizations.filter((o) => {
    if (formData.level === 'canton') return o.level === 'federal';
    if (formData.level === 'commune') return o.level === 'canton';
    return false;
  });

  const needsParent = formData.level !== 'federal';
  const canSubmit = formData.name.trim() && formData.code.trim() && (!needsParent || formData.parent_id);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setError('');
    setSubmitting(true);
    try {
      await onSubmit(formData);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save organization';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <div className={styles.formRow}>
        <input
          className={styles.input}
          placeholder="Name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
        />
        <input
          className={styles.input}
          placeholder="Code (e.g. GE, GE-LANCY)"
          value={formData.code}
          onChange={(e) => setFormData({ ...formData, code: e.target.value })}
          required
          disabled={isEdit}
        />
      </div>
      <div className={styles.formRow}>
        {isEdit ? (
          <input
            className={styles.input}
            value={formData.level.charAt(0).toUpperCase() + formData.level.slice(1)}
            disabled
          />
        ) : (
          <select
            className={styles.input}
            value={formData.level}
            onChange={(e) =>
              setFormData({ ...formData, level: e.target.value as OrgLevel, parent_id: null })
            }
          >
            {availableLevels.map((level) => (
              <option key={level} value={level}>
                {level.charAt(0).toUpperCase() + level.slice(1)}
              </option>
            ))}
          </select>
        )}
        {needsParent &&
          (isEdit ? (
            <input
              className={styles.input}
              value={
                organizations.find((o) => o.id === formData.parent_id)?.name ?? 'Unknown parent'
              }
              disabled
            />
          ) : possibleParents.length > 0 ? (
            <select
              className={styles.input}
              value={formData.parent_id ?? ''}
              onChange={(e) => setFormData({ ...formData, parent_id: e.target.value || null })}
              required
            >
              <option value="">-- Select parent --</option>
              {possibleParents.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.code})
                </option>
              ))}
            </select>
          ) : (
            <span
              style={{
                fontSize: 12,
                color: 'var(--color-text-muted)',
                alignSelf: 'center',
              }}
            >
              Create a {formData.level === 'commune' ? 'canton' : 'federal'} organization first
            </span>
          ))}
      </div>
      {error && (
        <div style={{ color: 'var(--color-danger)', fontSize: 12 }}>{error}</div>
      )}
      <div className={styles.formActions}>
        <button type="button" className={styles.actionBtn} onClick={onCancel} disabled={submitting}>
          Cancel
        </button>
        <button
          type="submit"
          className={styles.createBtn}
          disabled={!canSubmit || submitting}
        >
          {submitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Create'}
        </button>
      </div>
    </form>
  );
}
